import type { GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { CollectionFieldPath, CollectionFieldPathCollection, CollectionFieldPathItem } from '#layers/data-model/utils/collection-field-path';
import Fuse from 'fuse.js';
import { FUZZY_TOKEN_OPTIONS, matchesOrderedFuzzyTokens, normalizeCollectionFieldPathTokenSource } from '#layers/data-model/utils/collection-field-path-matching';

/*
 * Finds field paths without enumerating them.
 *
 * Field paths form a tree that can be infinite (a relation back to an earlier collection repeats
 * it) and is exponential even when finite: a few relations per collection reach thousands of
 * paths within a handful of hops. The schema behind that tree is small, though: collections are
 * nodes and relation fields are edges. This module answers questions about paths on that graph:
 *
 * - Reachability: "can a selectable field matching the query be reached from collection C in
 *   exactly N more hops, given the query tokens matched so far?" The answer depends only on
 *   (collection, remaining hops, matched query tokens), not on the route taken to C, so it is
 *   memoized on that key. Ruling out a branch therefore costs collections x hops x query length,
 *   however many routes lead into it.
 * - Enumeration: findPaths walks the tree but only descends into relations whose target can
 *   still reach a match, so each step leads to at least one result. Iterative deepening (all
 *   paths of 0 hops, then 1, ...) yields shorter paths first and lets callers stop early.
 *
 * Query matching is checked incrementally, one path segment at a time: each query token must
 * match a later label token than the one before, across display labels or API names. Taking the
 * earliest match per token finds a match whenever one exists, so the graph never drops a path
 * the ordered matchers in collection-field-path-matching accept. It can admit extra fuzzy paths,
 * which callers re-check.
 *
 * The graph only knows rules that belong to a single collection level (includeField, terminal
 * kinds, system collections). Path filters depend on the whole route, so callers check them on
 * the concrete paths findPaths yields, within a budget.
 */

interface CollectionFieldPathGraphRelation {
	id: GlobalSingleRelationFieldId;
	/** The collection the relation leads to. */
	target: CollectionFieldPathCollection;
}

interface CollectionFieldPathGraphLevelItem {
	item: CollectionFieldPathItem;
	/** Set when the item is a traversable relation. */
	relation?: CollectionFieldPathGraphRelation;
}

/** A path findPaths yields: its menu items, the selected leaf, and the field path they spell. */
export interface CollectionFieldPathGraphPath {
	items: CollectionFieldPathItem[];
	leaf: CollectionFieldPathItem;
	ids: CollectionFieldPath;
}

interface CollectionFieldPathGraphNode extends CollectionFieldPathGraphLevelItem {
	displayTokens: string[];
	apiTokens: string[];
}

export const FieldPathMatchMode = {
	ExactLeaf: 'exact-leaf',
	Prefix: 'prefix',
	Fuzzy: 'fuzzy',
} as const;

export interface CollectionFieldPathGraphQuery {
	tokens: string[];
	mode: typeof FieldPathMatchMode[keyof typeof FieldPathMatchMode];
}

interface FieldPathMatchPolicy {
	exactLeaf: boolean;
	fuzzyTokens: boolean;
	acceptsPath: (tokens: string[], path: CollectionFieldPathItem[]) => boolean;
}

/** Every mode chooses graph pruning and the final path check together. */
export const fieldPathMatchPolicies = {
	[FieldPathMatchMode.ExactLeaf]: { exactLeaf: true, fuzzyTokens: false, acceptsPath: () => true },
	[FieldPathMatchMode.Prefix]: { exactLeaf: false, fuzzyTokens: false, acceptsPath: () => true },
	[FieldPathMatchMode.Fuzzy]: {
		exactLeaf: false,
		fuzzyTokens: true,
		acceptsPath: (tokens, path) =>
			matchesOrderedFuzzyTokens(tokens, path.flatMap((item) => normalizeCollectionFieldPathTokenSource(item.displayName)))
			|| matchesOrderedFuzzyTokens(tokens, path.flatMap((item) => normalizeCollectionFieldPathTokenSource(item.apiName))),
	},
} satisfies Record<CollectionFieldPathGraphQuery['mode'], FieldPathMatchPolicy>;

export interface CollectionFieldPathGraph {
	/**
	 * Visits unfiltered selectable paths below `collection` that match `query`, shortest first,
	 * until `visit` returns false. `maxHops` counts relation hops below `collection`.
	 */
	findPaths: (
		collection: CollectionFieldPathCollection,
		query: CollectionFieldPathGraphQuery,
		maxHops: number,
		visit: (path: CollectionFieldPathGraphPath) => boolean,
	) => void;
}

const EMPTY_GRAPH_QUERY: CollectionFieldPathGraphQuery = { tokens: [], mode: FieldPathMatchMode.Prefix };

/**
 * Path-filter checks spent looking for an accepted descendant before a relation is kept anyway.
 * Filters that reject many reachable paths could otherwise make each menu level scan the schema.
 */
const DESCENDANT_CHECK_LIMIT = 200;

function isSelectableTerminal(node: CollectionFieldPathGraphNode): boolean {
	return !node.item.children || node.item.selectable === true;
}

/**
 * Creates a graph over the collections reachable from any root. `getLevel` lists one collection's
 * unfiltered menu items with the collection each relation leads to; the graph caches it per
 * collection object. Objects rather than ids key the caches because distinct views of one
 * collection can expose different fields.
 */
export function createCollectionFieldPathGraph(
	getLevel: (collection: CollectionFieldPathCollection) => CollectionFieldPathGraphLevelItem[],
): CollectionFieldPathGraph {
	const levels = new WeakMap<CollectionFieldPathCollection, CollectionFieldPathGraphNode[]>();
	const reachabilityByQuery = new Map<string, WeakMap<CollectionFieldPathCollection, Map<string, boolean>>>();
	const fuzzyTokenMatches = new Map<string, boolean>();

	function getNodes(collection: CollectionFieldPathCollection): CollectionFieldPathGraphNode[] {
		const cached = levels.get(collection);
		if (cached) return cached;

		const nodes = getLevel(collection).map(({ item, relation }) => ({
			item,
			relation,
			displayTokens: normalizeCollectionFieldPathTokenSource(item.displayName),
			apiTokens: normalizeCollectionFieldPathTokenSource(item.apiName),
		}));

		levels.set(collection, nodes);
		return nodes;
	}

	function tokenMatches(queryToken: string, token: string, fuzzy: boolean): boolean {
		if (!fuzzy) return token.startsWith(queryToken);

		const key = `${queryToken}\0${token}`;
		const cached = fuzzyTokenMatches.get(key);
		if (cached !== undefined) return cached;

		const matches = new Fuse([{ token }], FUZZY_TOKEN_OPTIONS).search(queryToken).length > 0;

		fuzzyTokenMatches.set(key, matches);
		return matches;
	}

	function findPaths(
		root: CollectionFieldPathCollection,
		query: CollectionFieldPathGraphQuery,
		maxHops: number,
		visit: (path: CollectionFieldPathGraphPath) => boolean,
	): void {
		const policy = fieldPathMatchPolicies[query.mode];
		const queryKey = `${query.mode}\0${query.tokens.join('\0')}`;
		let reachability = reachabilityByQuery.get(queryKey);

		if (!reachability) {
			reachability = new WeakMap();
			reachabilityByQuery.set(queryKey, reachability);
		}

		const reachable = reachability;
		const complete = query.tokens.length;
		const normalizedQuery = query.tokens.join(' ');
		let stopped = false;

		function matchesLeaf(node: CollectionFieldPathGraphNode): boolean {
			return !policy.exactLeaf || node.displayTokens.join(' ') === normalizedQuery || node.apiTokens.join(' ') === normalizedQuery;
		}

		// Greedy earliest matching per token admits every path the ordered token matchers accept.
		function advance(progress: number, tokens: string[]): number {
			for (const token of tokens) {
				const queryToken = query.tokens[progress];
				if (queryToken === undefined) break;
				if (tokenMatches(queryToken, token, policy.fuzzyTokens)) progress++;
			}

			return progress;
		}

		function canReach(collection: CollectionFieldPathCollection, hops: number, progress: number, channel: 'displayTokens' | 'apiTokens'): boolean {
			let cache = reachable.get(collection);

			if (!cache) {
				cache = new Map();
				reachable.set(collection, cache);
			}

			const key = `${hops}:${progress}:${channel}`;
			const cached = cache.get(key);
			if (cached !== undefined) return cached;

			const result = getNodes(collection).some((node) => {
				const next = advance(progress, node[channel]);
				if (hops === 0) return isSelectableTerminal(node) && matchesLeaf(node) && next === complete;
				return node.relation !== undefined && canReach(node.relation.target, hops - 1, next, channel);
			});

			cache.set(key, result);
			return result;
		}

		function walk(
			collection: CollectionFieldPathCollection,
			hops: number,
			displayProgress: number,
			apiProgress: number,
			prefixItems: CollectionFieldPathItem[],
			prefixIds: GlobalSingleRelationFieldId[],
		) {
			for (const node of getNodes(collection)) {
				if (stopped) return;

				const nextDisplay = advance(displayProgress, node.displayTokens);
				const nextApi = advance(apiProgress, node.apiTokens);

				if (hops > 0) {
					const { relation } = node;

					if (relation && (canReach(relation.target, hops - 1, nextDisplay, 'displayTokens')
						|| canReach(relation.target, hops - 1, nextApi, 'apiTokens'))) {
						walk(relation.target, hops - 1, nextDisplay, nextApi, [...prefixItems, node.item], [...prefixIds, relation.id]);
					}

					continue;
				}

				if (!isSelectableTerminal(node)) continue;
				if (!matchesLeaf(node)) continue;
				if (nextDisplay !== complete && nextApi !== complete) continue;
				if (!visit({ items: [...prefixItems, node.item], leaf: node.item, ids: [...prefixIds, node.item.id] })) stopped = true;
			}
		}

		// Iterative deepening yields short paths first without retaining an exponentially wide queue.
		for (let hops = 0; hops <= maxHops && !stopped; hops++) {
			if (canReach(root, hops, 0, 'displayTokens') || canReach(root, hops, 0, 'apiTokens')) {
				walk(root, hops, 0, 0, [], []);
			}
		}
	}

	return { findPaths };
}

export const AcceptedPathResult = {
	Accepted: 'accepted',
	None: 'none',
	Exhausted: 'exhausted',
} as const;

type AcceptedPathResult = typeof AcceptedPathResult[keyof typeof AcceptedPathResult];

/**
 * Finds whether `accepts` admits any path from `collection`, reached through the relations in `prefix`.
 * Reachability rules out collections without selectable fields exactly; `accepts` is then checked
 * on concrete paths, shortest first. Exhaustion leaves visibility policy to the caller.
 */
export function findAcceptedPath(
	graph: CollectionFieldPathGraph,
	accepts: (fieldPath: CollectionFieldPath) => boolean,
	collection: CollectionFieldPathCollection,
	prefix: GlobalSingleRelationFieldId[],
	maxHops: number,
): AcceptedPathResult {
	let result: AcceptedPathResult = AcceptedPathResult.None;
	let checks = 0;

	graph.findPaths(collection, EMPTY_GRAPH_QUERY, maxHops, (descendant) => {
		// Seeing one more candidate distinguishes exhaustion from exactly 200 rejected paths.
		if (checks === DESCENDANT_CHECK_LIMIT) {
			result = AcceptedPathResult.Exhausted;
			return false;
		}

		checks++;

		if (accepts([...prefix, ...descendant.ids]) !== false) {
			result = AcceptedPathResult.Accepted;
			return false;
		}

		return true;
	});

	return result;
}
